import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MainLayout } from "@/components/layout/MainLayout";
import { cn } from "@/lib/utils";
import { Check, ChevronRight, Target, MousePointer, ChevronUp, ChevronDown } from "lucide-react";
import { useInputMethod } from "@/contexts/InputMethodContext";
import { useTranslation } from "@/hooks/useTranslation";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";

// Setup stages
type SetupStage = "avatar" | "test" | "cursor";

// MediaPipe face landmarks
interface FaceLandmark {
  x: number;
  y: number;
  z: number;
}

export default function HeadTrackingSetup() {
  const navigate = useNavigate();
  const { t, dir } = useTranslation();
  const { updateCursorPosition } = useInputMethod();
  const [stage, setStage] = useState<SetupStage>("avatar");
  const [isTracking, setIsTracking] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [dwellProgress, setDwellProgress] = useState(0);
  const [faceDistance, setFaceDistance] = useState<"good" | "too_close" | "too_far">("good");
  const [faceLandmarks, setFaceLandmarks] = useState<FaceLandmark[] | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  
  // Check if existing setup exists
  const [hasExistingSetup, setHasExistingSetup] = useState(false);
  
  // Refs for DOM and MediaPipe
  const videoRef = useRef<HTMLVideoElement>(null);
  const meshCanvasRef = useRef<HTMLCanvasElement>(null);
  const faceLandmarkerRef = useRef<any>(null);
  const animationFrameRef = useRef<number>();
  const dwellTimerRef = useRef<NodeJS.Timeout>();
  const stageRef = useRef<SetupStage>(stage);
  
  // Edge Push Calibration refs (no state updates in animation loop)
  const yawOffsetRef = useRef(0);
  const pitchOffsetRef = useRef(0);
  const cursorRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const cursorElementRef = useRef<HTMLDivElement>(null);
  const lastUpdateRef = useRef(0);
  
  // Calibration constants - higher values = more cursor movement per head movement
  const SENSITIVITY_X = 100;
  const SENSITIVITY_Y = 80;
  const EDGE_MARGIN = 10;
  const SMOOTHING = 0.3;
  
  // Scroll buttons functionality
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const startScrolling = useCallback((direction: 'up' | 'down') => {
    if (scrollIntervalRef.current) return;
    
    const scrollAmount = direction === 'up' ? -40 : 40;
    scrollIntervalRef.current = setInterval(() => {
      window.scrollBy({ top: scrollAmount, behavior: 'auto' });
    }, 50);
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Keep stageRef in sync
  useEffect(() => {
    stageRef.current = stage;
    // Reset calibration offsets when stage changes
    yawOffsetRef.current = 0;
    pitchOffsetRef.current = 0;
  }, [stage]);

  // State for initialization status
  const [initError, setInitError] = useState<string | null>(null);

  // Check for existing setup completion on mount
  useEffect(() => {
    const wasSetupCompleted = localStorage.getItem("ayuni_head_tracking_setup_completed");
    if (wasSetupCompleted === "true") {
      setHasExistingSetup(true);
      console.log('[HeadTracking] Found existing setup completion');
    }
  }, []);

  // Initialize MediaPipe
  const initializeMediaPipe = useCallback(async () => {
    try {
      console.log('[HeadTracking] Loading MediaPipe...');
      const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
      
      const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
      
      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
        filesetResolver,
        getFaceLandmarkerOptions('GPU', true)
      );
      
      console.log('[HeadTracking] MediaPipe loaded successfully');
      return true;
    } catch (err) {
      console.log('[HeadTracking] GPU failed, trying CPU...', err);
      try {
        const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
        const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
        
        const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
        
        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
          filesetResolver,
          getFaceLandmarkerOptions('CPU', true)
        );
        
        console.log('[HeadTracking] MediaPipe loaded with CPU fallback');
        return true;
      } catch (err2) {
        console.error('[HeadTracking] MediaPipe failed:', err2);
        setInitError(t("faceModelError"));
        return false;
      }
    }
  }, [t]);

  // Setup webcam
  const setupWebcam = useCallback(async () => {
    try {
      console.log('[HeadTracking] Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        console.log('[HeadTracking] Camera started successfully');
        return true;
      }
      setInitError(t("videoError"));
      return false;
    } catch (err) {
      console.error('[HeadTracking] Camera error:', err);
      setInitError(t("cameraError"));
      return false;
    }
  }, [t]);

  // Calculate face distance based on eye distance
  const calculateFaceDistance = (landmarks: FaceLandmark[]): "good" | "too_close" | "too_far" => {
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const eyeDistance = Math.abs(rightEye.x - leftEye.x);
    
    if (eyeDistance > 0.25) return "too_close";
    if (eyeDistance < 0.12) return "too_far";
    return "good";
  };

  // Extract yaw and pitch from landmarks
  const extractHeadAngles = (landmarks: FaceLandmark[]) => {
    const noseTip = landmarks[1];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const forehead = landmarks[10];
    const chin = landmarks[152];
    
    const eyeDistance = rightEye.x - leftEye.x;
    const noseOffset = noseTip.x - (leftEye.x + rightEye.x) / 2;
    const yaw = (noseOffset / eyeDistance) * 60;
    
    const faceHeight = chin.y - forehead.y;
    const noseYOffset = noseTip.y - (forehead.y + chin.y) / 2;
    const pitch = (noseYOffset / faceHeight) * 60;
    
    return { yaw, pitch };
  };

  // Core tracking loop - updates cursor via DOM, not React state
  const runTrackingLoop = useCallback(() => {
    if (!videoRef.current || !faceLandmarkerRef.current) {
      animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
      return;
    }
    
    const video = videoRef.current;
    
    if (video.readyState >= 2) {
      const results = faceLandmarkerRef.current.detectForVideo(video, performance.now());
      
      if (results.faceLandmarks && results.faceLandmarks.length > 0) {
        const landmarks = results.faceLandmarks[0];
        const { yaw, pitch } = extractHeadAngles(landmarks);
        
        // Apply calibration offsets
        const calibratedYaw = yaw - yawOffsetRef.current;
        const calibratedPitch = pitch - pitchOffsetRef.current;
        
        // Calculate raw cursor position
        const screenCenterX = window.innerWidth / 2;
        const screenCenterY = window.innerHeight / 2;
        
        // Invert X because camera is mirrored
        let rawX = screenCenterX - (calibratedYaw * SENSITIVITY_X);
        let rawY = screenCenterY + (calibratedPitch * SENSITIVITY_Y);
        
        // Edge Push Calibration
        if (rawX < EDGE_MARGIN) {
          // User looking further left than current calibration allows
          yawOffsetRef.current += (EDGE_MARGIN - rawX) / SENSITIVITY_X * 0.5;
          rawX = EDGE_MARGIN;
        } else if (rawX > window.innerWidth - EDGE_MARGIN) {
          // User looking further right
          yawOffsetRef.current -= (rawX - (window.innerWidth - EDGE_MARGIN)) / SENSITIVITY_X * 0.5;
          rawX = window.innerWidth - EDGE_MARGIN;
        }
        
        if (rawY < EDGE_MARGIN) {
          // User looking further up - decrease offset to allow cursor to go back down
          pitchOffsetRef.current -= (EDGE_MARGIN - rawY) / SENSITIVITY_Y * 0.5;
          rawY = EDGE_MARGIN;
        } else if (rawY > window.innerHeight - EDGE_MARGIN) {
          // User looking further down - increase offset to allow cursor to go back up
          pitchOffsetRef.current += (rawY - (window.innerHeight - EDGE_MARGIN)) / SENSITIVITY_Y * 0.5;
          rawY = window.innerHeight - EDGE_MARGIN;
        }
        
        // Smooth cursor movement
        cursorRef.current.x += (rawX - cursorRef.current.x) * SMOOTHING;
        cursorRef.current.y += (rawY - cursorRef.current.y) * SMOOTHING;
        
        // Clamp to screen bounds
        cursorRef.current.x = Math.max(0, Math.min(window.innerWidth, cursorRef.current.x));
        cursorRef.current.y = Math.max(0, Math.min(window.innerHeight, cursorRef.current.y));
        
        // Update cursor DOM element directly (no React state)
        const currentStage = stageRef.current;
        if ((currentStage === "test" || currentStage === "cursor") && cursorElementRef.current) {
          cursorElementRef.current.style.left = `${cursorRef.current.x}px`;
          cursorElementRef.current.style.top = `${cursorRef.current.y}px`;
        }
        
        // Update cursor position state frequently for dwell detection
        setCursorPos({ x: cursorRef.current.x, y: cursorRef.current.y });
        
        // Also update InputMethodContext for RestMode dwell detection
        updateCursorPosition(cursorRef.current.x, cursorRef.current.y);
        
        // Throttled React state updates for face mesh rendering (every 100ms)
        const now = performance.now();
        if (now - lastUpdateRef.current > 100) {
          lastUpdateRef.current = now;
          setFaceLandmarks(landmarks);
          setFaceDistance(calculateFaceDistance(landmarks));
        }
      } else {
        // No face detected - throttled update
        const now = performance.now();
        if (now - lastUpdateRef.current > 100) {
          lastUpdateRef.current = now;
          setFaceLandmarks(null);
        }
      }
    }
    
    animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
  }, [updateCursorPosition]);

  // Start tracking
  const startTracking = useCallback(async () => {
    const mpReady = await initializeMediaPipe();
    if (!mpReady) return;
    
    const webcamReady = await setupWebcam();
    if (!webcamReady) return;
    
    setIsTracking(true);
    runTrackingLoop();
  }, [initializeMediaPipe, setupWebcam, runTrackingLoop]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
    
    setIsTracking(false);
  }, []);

  // Initialize on mount
  useEffect(() => {
    startTracking();
    return () => stopTracking();
  }, []);

  // Dwell detection for all interactive elements
  const currentTargetRef = useRef<HTMLElement | null>(null);
  
  useEffect(() => {
    if (stage !== "test" && stage !== "cursor") return;
    
    // Find interactive element under cursor
    const elementsAtPoint = document.elementsFromPoint(cursorPos.x, cursorPos.y);
    
    // Check if RestMode overlay is present (it has z-[99999])
    const restModeOverlay = elementsAtPoint.find(el => 
      el.classList.contains('bg-black') && 
      el.classList.contains('fixed') &&
      el.classList.contains('inset-0')
    );
    
    // If RestMode is active, only allow interaction with elements inside it
    // Elements inside RestMode will be found before the overlay itself in elementsAtPoint
    let interactiveElement: HTMLElement | undefined;
    
    if (restModeOverlay) {
      // Only find interactive elements that are inside the RestMode overlay
      interactiveElement = elementsAtPoint.find(el => {
        if (el === restModeOverlay) return false; // Skip the overlay itself
        const isInteractive = el.tagName === 'BUTTON' || 
          el.tagName === 'A' || 
          el.getAttribute('role') === 'button' ||
          el.classList.contains('clickable');
        // Check if element is inside the RestMode overlay (before it in the list)
        const overlayIndex = elementsAtPoint.indexOf(restModeOverlay);
        const elementIndex = elementsAtPoint.indexOf(el);
        return isInteractive && elementIndex < overlayIndex;
      }) as HTMLElement | undefined;
    } else {
      // Normal mode - find any interactive element
      interactiveElement = elementsAtPoint.find(el => 
        el.tagName === 'BUTTON' || 
        el.tagName === 'A' || 
        el.getAttribute('role') === 'button' ||
        el.classList.contains('clickable')
      ) as HTMLElement | undefined;
    }
    
    if (interactiveElement) {
      // Different target - reset
      if (currentTargetRef.current !== interactiveElement) {
        if (dwellTimerRef.current) {
          clearInterval(dwellTimerRef.current);
          dwellTimerRef.current = undefined;
        }
        setDwellProgress(0);
        currentTargetRef.current = interactiveElement;
      }
      
      // Start timer if not running
      if (!dwellTimerRef.current) {
        dwellTimerRef.current = setInterval(() => {
          setDwellProgress(prev => {
            if (prev >= 100) {
              clearInterval(dwellTimerRef.current!);
              dwellTimerRef.current = undefined;
              // Trigger click on the current target
              if (currentTargetRef.current) {
                currentTargetRef.current.click();
              }
              // Mark test as completed if on test stage and clicking the test target
              if (stage === "test" && currentTargetRef.current?.id === "test-target") {
                setTestCompleted(true);
              }
              currentTargetRef.current = null;
              return 0; // Reset for next interaction
            }
            return prev + 4; // ~1.25 seconds to fill
          });
        }, 50);
      }
    } else {
      // No interactive element - clear timer
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
        dwellTimerRef.current = undefined;
      }
      setDwellProgress(0);
      currentTargetRef.current = null;
    }
  }, [cursorPos, stage]);

  // Navigation handlers
  const handleBack = () => {
    if (stage === "test") {
      setStage("avatar");
    } else if (stage === "cursor") {
      setStage("test");
      setTestCompleted(false);
      setDwellProgress(0);
    } else {
      stopTracking();
      navigate("/control");
    }
  };

  const handleContinue = () => {
    if (stage === "avatar") {
      setStage("test");
    } else if (stage === "test" && testCompleted) {
      setStage("cursor");
    } else if (stage === "cursor") {
      // Mark setup as completed
      localStorage.setItem("ayuni_head_tracking_setup_completed", "true");
      // Save control method to localStorage and database
      const settings = getUserSettings();
      saveUserSettings({ ...settings, controlMethod: "head_tracking" });
      sessionStorage.setItem("controlMethod", "head_tracking");
      window.dispatchEvent(new CustomEvent("controlMethodChanged", { 
        detail: { method: "head_tracking" } 
      }));
      stopTracking();
      navigate("/");
    }
  };

  // Skip setup and use existing configuration
  const handleSkipSetup = () => {
    // Save control method to localStorage and database
    const settings = getUserSettings();
    saveUserSettings({ ...settings, controlMethod: "head_tracking" });
    sessionStorage.setItem("controlMethod", "head_tracking");
    window.dispatchEvent(new CustomEvent("controlMethodChanged", { 
      detail: { method: "head_tracking" } 
    }));
    stopTracking();
    navigate("/");
  };

  // Draw face mesh as dot grid
  const drawFaceMesh = useCallback((canvas: HTMLCanvasElement, landmarks: FaceLandmark[], color: string) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = color;
    
    const dotIndices = [
      10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 
      397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 
      172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
      70, 63, 105, 66, 107, 55, 65, 52, 53, 46,
      300, 293, 334, 296, 336, 285, 295, 282, 283, 276,
      33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246,
      362, 382, 381, 380, 374, 373, 390, 249, 263, 466, 388, 387, 386, 385, 384, 398,
      168, 6, 197, 195, 5, 4, 1, 19, 94, 2, 98, 327,
      61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 308, 324, 318, 402, 317, 14, 87, 178, 88, 95,
      117, 118, 119, 120, 121, 346, 347, 348, 349, 350,
      137, 138, 139, 140, 141, 366, 367, 368, 369, 370,
      68, 69, 104, 108, 151, 298, 299, 333, 337,
    ];
    
    dotIndices.forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    const keyPoints = [1, 33, 263, 61, 291, 168, 10, 152];
    keyPoints.forEach(idx => {
      const point = landmarks[idx];
      ctx.beginPath();
      ctx.arc(point.x * canvas.width, point.y * canvas.height, 2.5, 0, Math.PI * 2);
      ctx.fill();
    });
  }, []);

  // Update face mesh canvas
  useEffect(() => {
    if (stage === "avatar" && meshCanvasRef.current && faceLandmarks) {
      const color = faceDistance === "good" ? "#22c55e" : "#f97316";
      drawFaceMesh(meshCanvasRef.current, faceLandmarks, color);
    }
  }, [faceLandmarks, faceDistance, stage, drawFaceMesh]);

  const getDistanceMessage = () => {
    switch (faceDistance) {
      case "too_close": return t("tooClose");
      case "too_far": return t("tooFar");
      default: return t("distanceGood");
    }
  };

  const renderAvatarStage = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6">
      <h2 className="text-2xl font-bold text-foreground">{t("faceDetectionTitle")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("faceDetectionDesc")}
      </p>
      
      <div className="relative w-80 h-60 bg-black rounded-2xl overflow-hidden border-4 border-border">
        <canvas 
          ref={meshCanvasRef}
          width={320}
          height={240}
          className="w-full h-full"
        />
        {!faceLandmarks && isTracking && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-muted-foreground">{t("searchingForFace")}</p>
          </div>
        )}
        {initError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <p className="text-red-500 text-center px-4">{initError}</p>
          </div>
        )}
      </div>
      
      <div className={cn(
        "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
        initError 
          ? "bg-red-500/20 text-red-500"
          : faceDistance === "good" 
            ? "bg-green-500/20 text-green-500" 
            : "bg-orange-500/20 text-orange-500"
      )}>
        {initError ? initError : (isTracking ? getDistanceMessage() : t("initializingTracking"))}
      </div>
      
      {/* Skip setup option if existing setup exists */}
      {hasExistingSetup && faceDistance === "good" && (
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
          <p className="text-sm text-green-600 dark:text-green-400 mb-3 text-center">
            {t("foundPreviousSetup")}
          </p>
          <div className="flex gap-3 justify-center">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleSkipSetup}
              className="px-6 py-2 rounded-xl bg-green-500 text-white font-medium"
            >
              {t("startImmediately")}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setHasExistingSetup(false)}
              className="px-6 py-2 rounded-xl border border-border bg-card text-foreground font-medium"
            >
              {t("setupAgain")}
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );

  const renderTestStage = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6 relative">
      <h2 className="text-2xl font-bold text-foreground">{t("trackingTestTitle")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("trackingTestDesc")}
      </p>
      
      <motion.button
        id="test-target"
        className={cn(
          "relative px-12 py-6 rounded-2xl text-xl font-bold transition-all",
          testCompleted 
            ? "bg-accent text-accent-foreground" 
            : "bg-primary text-primary-foreground"
        )}
        whileHover={{ scale: 1.05 }}
        onClick={handleContinue}
        disabled={!testCompleted}
      >
        <Target className="w-6 h-6 inline-block ml-2" />
        {testCompleted ? t("excellentClickToContinue") : t("continue")}
        
        {dwellProgress > 0 && !testCompleted && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <rect
              x="2"
              y="2"
              width="calc(100% - 4px)"
              height="calc(100% - 4px)"
              rx="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeDasharray={`${dwellProgress * 4} 400`}
              className="text-accent"
            />
          </svg>
        )}
      </motion.button>
      
      {/* Virtual cursor with dwell ring - positioned via ref for performance */}
      <div
        ref={cursorElementRef}
        className="fixed pointer-events-none z-[999999]"
        style={{
          left: cursorRef.current.x,
          top: cursorRef.current.y,
          transform: "translate(-50%, -50%)"
        }}
      >
        <div className={`relative w-14 h-14 flex items-center justify-center ${dwellProgress > 0 ? 'scale-110' : ''} transition-transform duration-150`}>
          {/* Dwell progress ring */}
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
            {/* Background ring */}
            <circle
              cx="28"
              cy="28"
              r="22"
              fill="none"
              stroke="hsl(var(--border))"
              strokeWidth="6"
              opacity="0.4"
            />
            {/* Progress ring - orange fill */}
            <circle
              cx="28"
              cy="28"
              r="22"
              fill="none"
              stroke="hsl(var(--accent))"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${dwellProgress * 1.38} 138`}
              className="transition-all duration-75"
              style={{ 
                filter: dwellProgress > 0 ? 'drop-shadow(0 0 8px hsl(var(--accent)))' : 'none' 
              }}
            />
          </svg>
          {/* Center dot */}
          <div 
            className="w-4 h-4 rounded-full bg-white border-2 border-accent shadow-lg transition-all duration-150"
            style={{
              boxShadow: dwellProgress > 0 
                ? '0 0 12px hsl(var(--accent)), 0 2px 4px rgba(0,0,0,0.3)' 
                : '0 2px 4px rgba(0,0,0,0.3)'
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderCursorStage = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6">
      <h2 className="text-2xl font-bold text-foreground">{t("cursorActiveTitle")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("cursorActiveDesc")}
      </p>
      
      <div className="grid grid-cols-2 gap-4 mt-8">
        {[`${t("testButton")} 1`, `${t("testButton")} 2`, `${t("testButton")} 3`, `${t("testButton")} 4`].map((label, index) => (
          <DwellButton 
            key={index} 
            label={label} 
            cursorPos={cursorPos}
          />
        ))}
      </div>
      
      {/* Virtual cursor with dwell ring - positioned via ref for performance */}
      <div
        ref={cursorElementRef}
        className="fixed pointer-events-none z-[999999]"
        style={{
          left: cursorRef.current.x,
          top: cursorRef.current.y,
          transform: "translate(-50%, -50%)"
        }}
      >
        <DwellCursor cursorPos={cursorPos} />
      </div>
    </div>
  );

  return (
    <MainLayout>
      <video ref={videoRef} className="hidden" playsInline muted />
      
      <div className="flex-1 flex flex-col p-4 bg-background overflow-auto" dir={dir}>
        {/* Stage indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {(["avatar", "test", "cursor"] as SetupStage[]).map((s, i) => (
            <React.Fragment key={s}>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all",
                stage === s 
                  ? "bg-primary text-primary-foreground" 
                  : (["avatar", "test", "cursor"].indexOf(stage) > i 
                      ? "bg-accent text-accent-foreground" 
                      : "bg-muted text-muted-foreground")
              )}>
                {["avatar", "test", "cursor"].indexOf(stage) > i ? (
                  <Check className="w-4 h-4" />
                ) : (
                  i + 1
                )}
              </div>
              {i < 2 && (
                <div className={cn(
                  "w-12 h-1 rounded",
                  ["avatar", "test", "cursor"].indexOf(stage) > i 
                    ? "bg-accent" 
                    : "bg-muted"
                )} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 flex flex-col"
          >
            {stage === "avatar" && renderAvatarStage()}
            {stage === "test" && renderTestStage()}
            {stage === "cursor" && renderCursorStage()}
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation buttons */}
        <div className="flex gap-3 justify-center mt-6">
          <button
            onClick={handleBack}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-card hover:bg-muted border-2 border-border",
              "text-muted-foreground hover:text-foreground"
            )}
          >
            <ChevronRight className="w-5 h-5" />
            <span>{t("back")}</span>
          </button>

          {(stage === "avatar" || (stage === "test" && testCompleted) || stage === "cursor") && (
            <button
              onClick={handleContinue}
              className={cn(
                "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
                "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
              )}
            >
              <Check className="w-5 h-5" />
              <span>{stage === "cursor" ? t("finish") : t("continue")}</span>
            </button>
          )}
        </div>
        
        {/* Floating Scroll Buttons */}
        {(stage === "test" || stage === "cursor") && (
          <div className="fixed left-4 top-1/2 -translate-y-1/2 flex flex-col gap-4 z-50">
            <button
              onMouseEnter={() => startScrolling('up')}
              onMouseLeave={stopScrolling}
              onClick={() => window.scrollBy({ top: -200, behavior: 'smooth' })}
              className={cn(
                "w-16 h-16 rounded-2xl flex items-center justify-center",
                "bg-card border-2 border-border",
                "hover:border-accent hover:ring-4 hover:ring-accent/30 hover:scale-110",
                "transition-all duration-200 shadow-lg"
              )}
            >
              <ChevronUp className="w-8 h-8 text-primary" />
            </button>
            <button
              onMouseEnter={() => startScrolling('down')}
              onMouseLeave={stopScrolling}
              onClick={() => window.scrollBy({ top: 200, behavior: 'smooth' })}
              className={cn(
                "w-16 h-16 rounded-2xl flex items-center justify-center",
                "bg-card border-2 border-border",
                "hover:border-accent hover:ring-4 hover:ring-accent/30 hover:scale-110",
                "transition-all duration-200 shadow-lg"
              )}
            >
              <ChevronDown className="w-8 h-8 text-primary" />
            </button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

// Dwell button component for cursor stage
function DwellButton({ 
  label, 
  cursorPos 
}: { 
  label: string; 
  cursorPos: { x: number; y: number };
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dwellProgress, setDwellProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const dwellTimerRef = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    if (!buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    const isOver = 
      cursorPos.x >= rect.left &&
      cursorPos.x <= rect.right &&
      cursorPos.y >= rect.top &&
      cursorPos.y <= rect.bottom;
    
    setIsHovered(isOver);
    
    if (isOver) {
      if (!dwellTimerRef.current) {
        dwellTimerRef.current = setInterval(() => {
          setDwellProgress(prev => {
            if (prev >= 100) {
              clearInterval(dwellTimerRef.current!);
              dwellTimerRef.current = undefined;
              buttonRef.current?.click();
              return 0;
            }
            return prev + 5;
          });
        }, 50);
      }
    } else {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
        dwellTimerRef.current = undefined;
      }
      setDwellProgress(0);
    }
    
    return () => {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
      }
    };
  }, [cursorPos]);
  
  return (
    <button
      ref={buttonRef}
      className={cn(
        "relative px-8 py-6 rounded-xl text-lg font-medium transition-all",
        "bg-card border-2 hover:border-primary",
        isHovered ? "border-primary bg-primary/10" : "border-border"
      )}
    >
      {label}
      
      {dwellProgress > 0 && (
        <div 
          className="absolute bottom-0 left-0 h-1 bg-accent rounded-b-xl transition-all"
          style={{ width: `${dwellProgress}%` }}
        />
      )}
    </button>
  );
}

// Dwell cursor component with circular progress
function DwellCursor({ cursorPos }: { cursorPos: { x: number; y: number } }) {
  const [dwellProgress, setDwellProgress] = useState(0);
  const dwellTimerRef = useRef<NodeJS.Timeout>();
  const lastElementRef = useRef<Element | null>(null);
  const isActiveRef = useRef(false);
  
  useEffect(() => {
    // Check if cursor is over any interactive element
    const elementsAtPoint = document.elementsFromPoint(cursorPos.x, cursorPos.y);
    const interactive = elementsAtPoint.find(el => 
      el.tagName === 'BUTTON' || 
      el.tagName === 'A' || 
      el.getAttribute('role') === 'button' ||
      el.classList.contains('dwell-target')
    );
    
    if (interactive) {
      // Same element - don't restart timer
      if (interactive === lastElementRef.current && isActiveRef.current) {
        return;
      }
      
      // New element - start fresh
      lastElementRef.current = interactive;
      isActiveRef.current = true;
      setDwellProgress(0);
      
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
      }
      
      dwellTimerRef.current = setInterval(() => {
        setDwellProgress(prev => {
          if (prev >= 100) {
            clearInterval(dwellTimerRef.current!);
            dwellTimerRef.current = undefined;
            isActiveRef.current = false;
            (interactive as HTMLElement).click();
            lastElementRef.current = null;
            return 0;
          }
          return prev + 5;
        });
      }, 50);
    } else {
      // No interactive element - reset
      if (lastElementRef.current || isActiveRef.current) {
        lastElementRef.current = null;
        isActiveRef.current = false;
        setDwellProgress(0);
        if (dwellTimerRef.current) {
          clearInterval(dwellTimerRef.current);
          dwellTimerRef.current = undefined;
        }
      }
    }
  }, [cursorPos]);
  
  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
      }
    };
  }, []);
  
  return (
    <div className={`relative w-14 h-14 flex items-center justify-center ${dwellProgress > 0 ? 'scale-110' : ''} transition-transform duration-150`}>
      {/* Dwell progress ring */}
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
        {/* Background ring */}
        <circle
          cx="28"
          cy="28"
          r="22"
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="6"
          opacity="0.4"
        />
        {/* Progress ring - orange fill */}
        <circle
          cx="28"
          cy="28"
          r="22"
          fill="none"
          stroke="hsl(var(--accent))"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${dwellProgress * 1.38} 138`}
          className="transition-all duration-75"
          style={{ 
            filter: dwellProgress > 0 ? 'drop-shadow(0 0 8px hsl(var(--accent)))' : 'none' 
          }}
        />
      </svg>
      {/* Center dot */}
      <div 
        className="w-4 h-4 rounded-full bg-white border-2 border-accent shadow-lg transition-all duration-150"
        style={{
          boxShadow: dwellProgress > 0 
            ? '0 0 12px hsl(var(--accent)), 0 2px 4px rgba(0,0,0,0.3)' 
            : '0 2px 4px rgba(0,0,0,0.3)'
        }}
      />
    </div>
  );
}
